import React from 'react';
import {
  Grid,
  Card,
  Typography,
  Box,
  Stack,
  LinearProgress,
  Chip,
} from '@mui/material';
import { Server, HardDrive, Cpu, CheckCircle2, AlertTriangle } from 'lucide-react';

function formatBytes(bytes, decimals = 1) {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

export default function NodeHealthCard({ nodes = [] }) {
  if (!nodes || nodes.length === 0) return null;

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {nodes.map((node) => {
        const memPercent =
          node.memLimit > 0
            ? Math.min(100, Math.round((node.memUsed / node.memLimit) * 100))
            : 0;

        const isMemHigh = memPercent > 80;

        return (
          <Grid item xs={12} md={6} lg={4} key={node.name}>
            <Card sx={{ p: 2, height: '100%' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Box
                    sx={{
                      p: 0.8,
                      borderRadius: 1.5,
                      bgcolor: 'rgba(2, 132, 199, 0.1)',
                      color: '#0284c7',
                      display: 'flex',
                    }}
                  >
                    <Server style={{ width: 16, height: 16 }} />
                  </Box>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 700, maxWidth: 180 }} noWrap>
                      {node.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: '0.65rem' }}>
                      Broker Node
                    </Typography>
                  </Box>
                </Stack>

                <Chip
                  size="small"
                  icon={
                    node.running ? (
                      <CheckCircle2 style={{ width: 12, height: 12, color: '#059669' }} />
                    ) : (
                      <AlertTriangle style={{ width: 12, height: 12, color: '#dc2626' }} />
                    )
                  }
                  label={node.running ? 'Running' : 'Offline'}
                  sx={{
                    height: 22,
                    fontSize: '0.7rem',
                    bgcolor: node.running ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                    color: node.running ? '#059669' : '#dc2626',
                    fontWeight: 600,
                  }}
                />
              </Stack>

              {/* Memory Gauge */}
              <Box mb={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Cpu style={{ width: 12, height: 12, color: '#64748b' }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      Memory
                    </Typography>
                  </Stack>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                    {formatBytes(node.memUsed)} / {formatBytes(node.memLimit)} ({memPercent}%)
                  </Typography>
                </Stack>

                <LinearProgress
                  variant="determinate"
                  value={memPercent}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    bgcolor: '#f1f5f9',
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 3,
                      bgcolor: isMemHigh ? '#d97706' : '#006951',
                    },
                  }}
                />
              </Box>

              {/* Disk Free */}
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                pt={1.5}
                sx={{ borderTop: '1px solid', borderColor: 'divider' }}
              >
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <HardDrive style={{ width: 12, height: 12, color: '#64748b' }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Free Disk Space
                  </Typography>
                </Stack>
                <Typography variant="caption" sx={{ color: '#059669', fontFamily: 'monospace', fontWeight: 700 }}>
                  {formatBytes(node.diskFree)}
                </Typography>
              </Stack>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
}
